import React, { useState, useEffect } from "react";
import {
	validateInitialRegistrationData,
	validateVerificationCode,
} from "../../services/dashApiService";
import { toast } from "react-toastify";
import { GenericButton } from "../../components/common/GenericButton";
import { ToggleIcon } from "../../components/common/ToggleIcon";
import { validateNhsno } from "../../utils";

export const InitialData = ({ onDataComplete, context, data }) => {
	const [nhsno, setNhsno] = useState("");
	const [email, setEmail] = useState("");
	const [nhsnoIcon, setNhsnoIcon] = useState();
	const [verificationCode, setVerificationCode] = useState("");
	const [disableValidateButton, setDisableValidateButton] = useState(true);
	const [disableVerifyButton, setDisableVerifyButton] = useState(true);

	const emailChange = (e) => {
		const value = e.target.value.replace(/ /g, "");
		setEmail(value);

		setDisableValidateButton(false);
		setDisableVerifyButton(true);
	};

	const nhsnoChange = (e) => {
		const value = e.target.value;
		setNhsno(value);
		setDisableVerifyButton(true);

		// does it look valid
		if (value.trim().length === 0) {
			setNhsnoIcon();
		} else {
			setNhsnoIcon(
				validateNhsno(value.replace(/[^0-9]/g, "")) ? "tick" : "cross"
			);
		}
	};

	const verificationCodeChange = (e) => {
		const value = e.target.value;
		setVerificationCode(value.replace(/[^0-9]/g));
	};

	const handleVerifyClick = () => {
		let aborted = false;

		validateVerificationCode(email, verificationCode)
			.then((result) => {
				if (aborted) {
					return;
				}

				let text;
				let autoClose = false;
				const type = result.success ? "success" : "warning";

				if (result.success) {
					onDataComplete(context, {
						email,
						nhsno,
						verificationCode,
						jwt: result.data.jwt,
					});

					autoClose = 20 * 1000;

					text = (
						<>
							<p className="mb-2">
								You have successfully verified your email and NHS/CHI no.
							</p>

							<p className="mb-2">You can now set up your password.</p>
						</>
					);
				} else {
					text = (
						<>
							<p className="mb-2">Your details could not be verified.</p>

							<p className="mb-2">
								This may occur if the code timed out or you mis-typed it. Please
								check the code and request a new one, if necessary.
							</p>
						</>
					);
				}

				if (type !== "success") {
					setDisableValidateButton(false);
				}

				toast(text, {
					type,
					autoClose,
				});
			})
			.catch((err) => {
				if (aborted) {
					return;
				}

				const text = (
					<>
						<p className="mb-2">Your email address has not been verified.</p>

						<p className="mb-2">
							This may occur if the code timed out before you could use it.
							Please re-validate your details to receive a new code, and try
							again.
						</p>
					</>
				);

				setDisableValidateButton(false);

				toast(text, {
					type: "error",
					hideProgressBar: true,
					autoClose: false,
				});
			});

		return () => {
			aborted = true;
		};
	};

	const handleValidateClick = () => {
		setDisableValidateButton(true);
		setDisableVerifyButton(true);

		let aborted = false;

		validateInitialRegistrationData(
			email.toLowerCase(),
			nhsno.replace(/[^0-9]/g, "")
		)
			.then((result) => {
				if (aborted) {
					return;
				}

				const data = result.data || {};
				const verdict = (data.result || data.status || "").toLowerCase();
				const suggestion = data.suggestion || data.suggested_correction;

				let severity = "warning";
				let text = (
					<>
						<p className="mb-2">There was a problem checking your details.</p>
						<p className="mb-2">Please try again shortly.</p>
					</>
				);

				switch (true) {
					case data.message && data.message.length > 0:
						severity = "warning";
						if (typeof data.message === "string") {
							text = data.message;
						} else {
							text = (
								<>
									{data.message.map((msg) => {
										return <p className="mb-2">{msg}</p>;
									})}
								</>
							);
						}
						break;

					case verdict.length > 0:
						if (verdict === "invalid") {
							severity = "error";
							text = (
								<>
									<p className="mb-2">{`Your email`}</p>
									<p className="mb-2">{`${email}`}</p>
									<p className="mb-2">{`is invalid`}</p>
									{suggestion && (
										<p className="mb-2">{`You could try ${suggestion}`}</p>
									)}
								</>
							);
						} else {
							severity = "success";
							text = (
								<>
									<p className="mb-2">{`An email with a verification code has been sent to ${email}`}</p>
									<p className="mb-2">
										Copy the code, paste it into the Code box and press Verify
									</p>
								</>
							);
						}
						break;

					case data.rateLimit === true:
						severity = "warning";
						break;

					case data.responseError === true:
						severity = "warning";
						break;

					case data.generalError === true:
					default:
						severity = "warning";
						break;
				}

				toast(text, {
					type: severity,
					hideProgressBar: true,
					// autoClose: false,
				});

				setDisableVerifyButton(severity !== "success");
			})
			.catch((err) => {
				if (aborted) {
					return;
				}

				const text = (
					<>
						<p className="mb-2">There was an error checking your details.</p>
						<p className="mb-2">Please try again shortly.</p>
					</>
				);
				toast(text, {
					type: "error",
					hideProgressBar: true,
					autoClose: false,
				});
			})
			.finally(() => {
				setDisableValidateButton(false);
			});

		return () => {
			aborted = true;
		};
	};

	useEffect(() => {
		if (!data) {
			return;
		}

		setNhsno(data.nhsno);
		setEmail(data.email);
		setVerificationCode(data.verificationCode);

		nhsnoChange({ target: { value: data.nhsno } });
	}, [data]);

	const _disableValidateButton =
		!email.trim() || nhsnoIcon !== "tick" || disableValidateButton;

	return (
		<div className="text-sm lg:text-base text-black">
			<h2 className={`text-2xl font-bold mb-6 text-center mt-2`}>
				Initial Details
			</h2>

			<div className={"text-sm leading-tight lg:leading-6"}>
				<div className={"mb-4"}>
					<p className={"mb-2"}>Your email address will be your username.</p>
					<p className={"mb-2"}>
						Enter your email address and your NHS / CHI number. Then press the
						"Request code" button.
					</p>
					<p className={"mb-2"}>
						We will send you an email containing a 3 digit verification code.
						You may need to check your Junk and Spam folders.
					</p>
					<p className={"mb-2"}>
						Copy the code into the box at the bottom of the screen and press
						Verify.
					</p>
				</div>

				<div className={"formInputContainer mt-10 mb-3"}>
					<label className={"basicFormLabel"} htmlFor={"email"}>
						Email<sup>*</sup>
					</label>
					<input
						type="email"
						className={"formField"}
						id="email"
						name="email"
						value={email}
						onChange={emailChange}
						placeholder="Address"
					/>
				</div>

				<div className={"formInputContainer mb-6"}>
					<label className={"basicFormLabel"} htmlFor={"nhsno"}>
						NHS/CHI no.<sup>*</sup>
					</label>
					<div className="relative">
						<input
							type="text"
							className={"formField"}
							id="nhsno"
							name="nhsno"
							value={nhsno}
							onChange={nhsnoChange}
							placeholder="NHS / CHI number - 10 digits"
						/>
						<ToggleIcon
							selection={nhsnoIcon}
							classes="ml-2 text-xl absolute right-2 top-3"
							colours={{ tick: "green", cross: "red" }}
						/>
					</div>
				</div>

				<div className={"flex justify-center max-w-xs mx-auto pb-4"}>
					<div className={"w-1/2"}>
						<GenericButton
							text="Request code"
							disabled={_disableValidateButton}
							onClick={handleValidateClick}
							classes="active-button"
						/>
					</div>
				</div>

				<div className={"formInputContainer mt-4 mb-6"}>
					<label className={"basicFormLabel"} htmlFor={"nhsno"}>
						Code<sup>*</sup>
					</label>
					<input
						type="text"
						disabled={disableVerifyButton}
						className={"formField"}
						id="verificationcode"
						name="verificationcode"
						value={verificationCode}
						onChange={verificationCodeChange}
						placeholder="Verification code - 3 digits"
					/>
				</div>

				<div className={"flex justify-center max-w-xs mx-auto"}>
					<div className={"w-1/2"}>
						<GenericButton
							text="Verify"
							disabled={disableVerifyButton}
							onClick={handleVerifyClick}
							classes="active-button"
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
